/* eslint-disable @typescript-eslint/naming-convention -- these are UPPER_SNAKE constant maps whose keys are
   lookup tokens (error markers, error codes, message ids), not object fields. */
import { HttpStatusCode, UserRole } from '@logz-pkg/enums';
import { sessionStateService } from '../../state/session.state.service';
import { AGENT_HUB_AGENTS_PATH, ORIONIQ_CAPPING_SETTINGS_PATH } from './agent-display.utils';

// Shared chat error resolution (ORIONIQ-1538 B1a). Cap-limit copy is billing-adjacent user-facing text whose
// admin/non-admin split decides which remediation a user is told to take — a second copy would eventually
// explain the same 429 two different ways, so every chat shell resolves errors through this module.

type ErrorBody = { message?: string; statusCode?: number; parameters?: { errorCode?: string } };

export type CapErrorShape = {
  message?: string;
  status?: number;
  response?: { status?: number; data?: ErrorBody };
  data?: ErrorBody;
};

/** `undefined` message = nothing better than the drawer's generic copy. */
export interface IChatErrorResolution {
  message?: string;
  isCap: boolean;
}

export const CAP_ERROR_MARKERS = {
  INVOCATION_LIMIT_REACHED: 'Invocation limit reached',
  CHAT_BUDGET_LIMIT_REACHED: 'Chat budget limit reached',
  SYSTEM_DAILY_ACCOUNT_LIMIT: 'system_daily_account_limit',
  AGENT_DAILY_LIMIT: 'agent_daily_limit',
  ACCOUNT_MONTHLY_BUDGET: 'account_monthly_budget',
  NON_PAYING_TOTAL_BUDGET: 'non_paying_total_budget',
  NON_PAYING_DAILY_BUDGET: 'non_paying_daily_budget',
  NON_PAYING_BUDGET_UNKNOWN: 'non_paying_budget_unknown',
} as const;

export const CHAT_ERROR_CODES = {
  AI_DISABLED: 'AI_DISABLED',
  FETCH_ERROR: 'FETCH_ERROR',
  NO_ACCOUNT: 'NO_ACCOUNT',
} as const;

export const AGENT_ERROR_MARKERS = {
  NOT_FOUND: 'Agent not found',
  NOT_ACTIVE: 'Agent is not active',
} as const;

export const REQUEST_TOO_LARGE_MESSAGE =
  'The data attached to this request is too large for the AI to process. Filter the page to narrow the data, then ask again.';

export const CAP_LIMIT_MESSAGES = {
  SYSTEM_DAILY:
    "You've reached your account's daily AI agents invocation limit. Contact support to request increasing the daily limit.",
  AGENT_DAILY: `You've reached your daily AI agent invocation limit. [Adjust limits in Agents Hub](${AGENT_HUB_AGENTS_PATH}).`,
  MONTHLY_BUDGET_ADMIN: `You've reached your monthly AI budget. [Update the monthly budget in OrionIQ settings](${ORIONIQ_CAPPING_SETTINGS_PATH}).`,
  MONTHLY_BUDGET_NON_ADMIN:
    "You've reached your monthly AI budget. Contact your account admin to raise it in OrionIQ settings.",
  CHAT_BUDGET_ADMIN: `You've reached your monthly AI chat budget. [Update the budget in OrionIQ settings](${ORIONIQ_CAPPING_SETTINGS_PATH}).`,
  CHAT_BUDGET_NON_ADMIN:
    "You've reached your monthly AI chat budget. Contact your account admin to raise it in OrionIQ settings.",
  NON_PAYING_TOTAL_BUDGET:
    "You've used your full AI budget. [Upgrade your plan](/360/settings/plan-and-billing/plan) to keep using AI chat and AI agents.",
  NON_PAYING_DAILY_BUDGET:
    "You've used today's AI budget. It resets at midnight UTC — [upgrade your plan](/360/settings/plan-and-billing/plan) for uninterrupted access.",
  NON_PAYING_BUDGET_UNKNOWN:
    "We couldn't verify your AI usage just now, so AI is paused. This usually clears in a few minutes — please try again.",
  GENERIC_ADMIN: `You've reached your AI usage limit. [Review the limits in OrionIQ settings](${ORIONIQ_CAPPING_SETTINGS_PATH}).`,
  GENERIC_NON_ADMIN: "You've reached your AI usage limit. Contact your account admin for assistance.",
} as const;

export const CHAT_ERROR_MESSAGES = {
  AI_DISABLED: 'AI features are turned off for this account. Contact Logz.io support to enable them.',
  SETTINGS_UNAVAILABLE:
    "We couldn't verify this account's AI settings, so AI is paused. This usually clears in a few minutes — please try again.",
  KILL_SWITCH: 'AI agents are temporarily unavailable. Please try again shortly, or contact support if it persists.',
  AGENT_NOT_FOUND: "This account's OrionIQ assistant isn't set up yet. Contact support if this persists.",
  AGENT_INACTIVE: `This account's OrionIQ assistant is deactivated. An account admin can re-activate it in the [Agents Hub](${AGENT_HUB_AGENTS_PATH}).`,
} as const;

const isCapErrorShape = (error: unknown): error is CapErrorShape =>
  typeof error === 'object' && error !== null && ('data' in error || 'response' in error || 'message' in error);

const extractServerMessage = (error: unknown): string => {
  if (!isCapErrorShape(error)) return '';

  return error.response?.data?.message ?? error.data?.message ?? error.message ?? '';
};

export const isCapLimitError = (error: unknown): boolean =>
  [CAP_ERROR_MARKERS.INVOCATION_LIMIT_REACHED, CAP_ERROR_MARKERS.CHAT_BUDGET_LIMIT_REACHED].some(m =>
    extractServerMessage(error).includes(m),
  );

const extractStatus = (error: unknown): number | undefined => {
  if (!isCapErrorShape(error)) return undefined;

  return error.response?.status ?? error.status ?? error.response?.data?.statusCode ?? error.data?.statusCode;
};

const extractErrorCode = (error: unknown): string | undefined => {
  if (!isCapErrorShape(error)) return undefined;

  return error.response?.data?.parameters?.errorCode ?? error.data?.parameters?.errorCode;
};

export const isRequestTooLargeError = (error: unknown): boolean =>
  extractStatus(error) === HttpStatusCode.PAYLOAD_TOO_LARGE ||
  extractServerMessage(error).toLowerCase().includes('request entity too large');

export const getCapLimitMessage = (error: unknown): string => {
  const serverMsg = extractServerMessage(error);
  const isAdmin = sessionStateService.session.get()?.loggedInUser?.role === UserRole.Admin;

  // Non-paying reasons come first: their budget is fixed, so no "raise your limit" copy applies.
  if (serverMsg.includes(CAP_ERROR_MARKERS.NON_PAYING_BUDGET_UNKNOWN)) {
    return CAP_LIMIT_MESSAGES.NON_PAYING_BUDGET_UNKNOWN;
  }

  if (serverMsg.includes(CAP_ERROR_MARKERS.NON_PAYING_TOTAL_BUDGET)) return CAP_LIMIT_MESSAGES.NON_PAYING_TOTAL_BUDGET;

  if (serverMsg.includes(CAP_ERROR_MARKERS.NON_PAYING_DAILY_BUDGET)) return CAP_LIMIT_MESSAGES.NON_PAYING_DAILY_BUDGET;

  if (serverMsg.includes(CAP_ERROR_MARKERS.SYSTEM_DAILY_ACCOUNT_LIMIT)) return CAP_LIMIT_MESSAGES.SYSTEM_DAILY;

  if (serverMsg.includes(CAP_ERROR_MARKERS.AGENT_DAILY_LIMIT)) return CAP_LIMIT_MESSAGES.AGENT_DAILY;

  if (serverMsg.includes(CAP_ERROR_MARKERS.ACCOUNT_MONTHLY_BUDGET))
    return isAdmin ? CAP_LIMIT_MESSAGES.MONTHLY_BUDGET_ADMIN : CAP_LIMIT_MESSAGES.MONTHLY_BUDGET_NON_ADMIN;

  if (serverMsg.includes(CAP_ERROR_MARKERS.CHAT_BUDGET_LIMIT_REACHED))
    return isAdmin ? CAP_LIMIT_MESSAGES.CHAT_BUDGET_ADMIN : CAP_LIMIT_MESSAGES.CHAT_BUDGET_NON_ADMIN;

  return isAdmin ? CAP_LIMIT_MESSAGES.GENERIC_ADMIN : CAP_LIMIT_MESSAGES.GENERIC_NON_ADMIN;
};

const resolveMessage = (error: unknown, status: number | undefined): string | undefined => {
  if (isRequestTooLargeError(error)) return REQUEST_TOO_LARGE_MESSAGE;

  const errorCode = extractErrorCode(error);

  if (errorCode === CHAT_ERROR_CODES.AI_DISABLED) return CHAT_ERROR_MESSAGES.AI_DISABLED;

  if (errorCode === CHAT_ERROR_CODES.FETCH_ERROR || errorCode === CHAT_ERROR_CODES.NO_ACCOUNT) {
    return CHAT_ERROR_MESSAGES.SETTINGS_UNAVAILABLE;
  }

  if (status === HttpStatusCode.SERVICE_UNAVAILABLE) return CHAT_ERROR_MESSAGES.KILL_SWITCH;

  const serverMessage = extractServerMessage(error);

  if (status === HttpStatusCode.NOT_FOUND && serverMessage.includes(AGENT_ERROR_MARKERS.NOT_FOUND)) {
    return CHAT_ERROR_MESSAGES.AGENT_NOT_FOUND;
  }

  if (status === HttpStatusCode.FORBIDDEN && serverMessage.includes(AGENT_ERROR_MARKERS.NOT_ACTIVE)) {
    return CHAT_ERROR_MESSAGES.AGENT_INACTIVE;
  }

  return undefined;
};

export const resolveChatError = (error: unknown): IChatErrorResolution => {
  const status = extractStatus(error);
  const isCap = status === HttpStatusCode.TOO_MANY_REQUEST || isCapLimitError(error);

  return { isCap, message: isCap ? getCapLimitMessage(error) : resolveMessage(error, status) };
};
